import type { ComponentType } from "react";

import * as React from "react";
import { isValidElement } from "react";

import type { ITooltipParams } from "ag-grid-enterprise";
import type { AgGridReactProps } from "ag-grid-react";

import { themeQuartz } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";

import { cn } from "../../utils";
import { tooltipContentClassName } from "./tooltip";

interface AgGridTooltipProps<TData, TValue> extends ITooltipParams<
  TData,
  TValue
> {
  className?: string;
}

const getTooltipContent = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (isValidElement(value)) {
    return value;
  }

  return String(value);
};

const resolveTooltipContent = <TData, TValue>(
  props: AgGridTooltipProps<TData, TValue>,
) => {
  const directContent = getTooltipContent(props.valueFormatted ?? props.value);

  if (directContent) {
    return directContent;
  }

  const headerContent = getTooltipContent(props.colDef?.headerTooltip);

  if (headerContent) {
    return headerContent;
  }

  const tooltipValueGetter =
    props.colDef && "tooltipValueGetter" in props.colDef
      ? props.colDef.tooltipValueGetter
      : undefined;

  if (typeof tooltipValueGetter === "function") {
    return getTooltipContent(tooltipValueGetter(props));
  }

  return null;
};

export const AgGridTooltip = <TData, TValue>({
  className,
  ...props
}: AgGridTooltipProps<TData, TValue>) => {
  const content = resolveTooltipContent(props);

  if (!content) {
    return null;
  }

  return (
    <div
      className={cn(
        tooltipContentClassName,
        "af-pointer-events-none af-max-w-80",
        className,
      )}
    >
      {content}
    </div>
  );
};

export type DataTableProps<TData> = AgGridReactProps<TData>;

export type DataTableType<TData = unknown> = ComponentType<
  DataTableProps<TData> & { ref?: React.Ref<AgGridReact<TData>> }
>;

export const DataTable = (<TData,>({
  ref,
  className,
  defaultColDef,
  ...props
}: DataTableProps<TData> & {
  ref?: React.RefObject<AgGridReact<TData>>;
}) => (
  <AgGridReact<TData>
    className={cn("af-data-table", className)}
    //Customizing in data-table.css
    theme={themeQuartz}
    defaultColDef={{ tooltipComponent: AgGridTooltip, ...defaultColDef }}
    ref={ref}
    {...props}
  />
)) as (<TData>(
  props: DataTableProps<TData> & { ref?: React.Ref<AgGridReact<TData>> },
) => React.ReactElement) & { displayName?: string };
DataTable.displayName = "DataTable";
